import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, TrendingUp, TrendingDown, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_INFO = {
  CLP: { name: 'Chile', currency: 'Peso Chileno', flag: '\u{1F1E8}\u{1F1F1}', symbol: '$' },
  BOB: { name: 'Bolivia', currency: 'Boliviano', flag: '\u{1F1E7}\u{1F1F4}', symbol: 'Bs' },
  ARS: { name: 'Argentina', currency: 'Peso Argentino', flag: '\u{1F1E6}\u{1F1F7}', symbol: '$' },
  PEN: { name: 'Peru', currency: 'Sol Peruano', flag: '\u{1F1F5}\u{1F1EA}', symbol: 'S/' },
  VES: { name: 'Venezuela', currency: 'Bolivar', flag: '\u{1F1FB}\u{1F1EA}', symbol: 'Bs.D' },
  COP: { name: 'Colombia', currency: 'Peso Colombiano', flag: '\u{1F1E8}\u{1F1F4}', symbol: '$' },
};

const PublicRates = () => {
  const navigate = useNavigate();
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchRates = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${API}/api/exchange/rates/all-countries`);
      const data = await res.json();
      setRates(data.rates || {});
      setLastUpdate(new Date());
    } catch (e) {
      console.error('Error fetching rates:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRates();
    const interval = setInterval(() => fetchRates(), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatRate = (val) => {
    if (!val) return '-';
    if (val >= 100) return val.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2, timeZone: 'America/Santiago' });
    return val.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 4, timeZone: 'America/Santiago' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-t-transparent rounded-full mx-auto mb-3" style={{ borderColor: '#008000', borderTopColor: 'transparent' }} />
          <p className="text-gray-600 font-medium">Cargando tasas...</p>
        </div>
      </div>
    );
  }

  const orderedKeys = ['CLP', 'BOB', 'ARS', 'PEN', 'COP', 'VES'].filter(k => rates && rates[k]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition"
              data-testid="back-button"
            >
              <ArrowLeft size={18} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900" data-testid="page-title">Tasas de Cambio</h1>
              <p className="text-xs text-gray-500">USDT / Moneda local - Mercado P2P</p>
            </div>
          </div>
          <button
            onClick={() => fetchRates(true)}
            disabled={refreshing}
            className="p-2 rounded-lg hover:bg-gray-100 transition"
            data-testid="refresh-button"
          >
            <RefreshCw size={18} className={`text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Info banner */}
        <div className="rounded-xl p-4 mb-5 border" style={{ backgroundColor: '#f0fdf0', borderColor: '#bbf7d0' }}>
          <p className="text-sm font-medium" style={{ color: '#166534' }}>
            Tasas referenciales obtenidas del mercado P2P (promedio 5 mejores ofertas).
          </p>
          {lastUpdate && (
            <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#15803d' }}>
              <Clock size={12} /> Actualizado: {lastUpdate.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' })}
            </p>
          )}
        </div>

        {/* Rates cards */}
        <div className="space-y-3">
          {orderedKeys.map(code => {
            const info = COUNTRY_INFO[code];
            const r = rates[code];
            if (!info || !r) return null;
            const buy = r.buy;
            const sell = r.sell;
            const spread = buy && sell ? ((sell - buy) / buy * 100).toFixed(2) : null;

            return (
              <div
                key={code}
                className="bg-white rounded-xl border p-4 hover:shadow-md transition"
                data-testid={`rate-card-${code}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{info.flag}</span>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">{info.name}</p>
                      <p className="text-xs text-gray-400">{code} - {info.currency}</p>
                    </div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-semibold">
                    1 USDT
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <TrendingUp size={12} className="text-green-600" />
                      <span className="text-[10px] font-bold text-green-700 uppercase">Compra</span>
                    </div>
                    <p className="text-lg font-black text-green-800">
                      {info.symbol} {formatRate(buy)}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <TrendingDown size={12} className="text-orange-600" />
                      <span className="text-[10px] font-bold text-orange-700 uppercase">Venta</span>
                    </div>
                    <p className="text-lg font-black text-orange-800">
                      {info.symbol} {formatRate(sell)}
                    </p>
                  </div>
                </div>

                {spread && (
                  <div className="mt-2 text-center">
                    <span className="text-[10px] text-gray-400">Spread: {spread}%</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-400 mb-4">
            Las tasas se actualizan cada 5 minutos. Son referenciales y pueden variar al momento de tu transferencia.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="text-white font-bold py-3 px-8 rounded-xl transition hover:opacity-90 text-sm"
            style={{ backgroundColor: '#008000' }}
            data-testid="register-cta"
          >
            Crear cuenta y enviar dinero
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicRates;
